import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

function normalizeFolderPrefix(value: string): string {
  const trimmed = value.trim().replace(/^\/+/, "").replace(/\/+$/, "");
  return trimmed ? `${trimmed}/` : "";
}

function normalizeSubfolderSegment(value: string): string {
  return value.trim().replace(/^\/+/, "").replace(/\/+$/, "");
}

const folderPrefixSchema = z
  .string()
  .transform(normalizeFolderPrefix)
  .pipe(z.string().min(1, "Folder prefix is required"));

const destinationSubfolderSchema = z
  .string()
  .transform(normalizeSubfolderSegment)
  .pipe(
    z
      .string()
      .min(1, "Destination subfolder is required")
      .regex(/^[^/]+$/, "Destination subfolder must be a single path segment"),
  );

export const archiveShipAndDebitAzureFilesConfigSchema = z.object({
  containerName: z
    .string()
    .prefault("rexel-internal")
    .describe("Azure blob container to scan"),
  folderPrefixes: z
    .array(folderPrefixSchema)
    .min(1)
    .describe("Division folder prefixes to scan, e.g. CWG/ or CQE/"),
  allFolderPrefixes: z
    .array(
      z.object({
        id: z.string(),
        prefix: z.string(),
        enabled: z.boolean(),
      }),
    )
    .optional()
    .describe(
      "UI-oriented folder list that preserves disabled folder prefixes while editing the workflow.",
    ),
  fileNamePatterns: z
    .array(z.string().trim().min(1, "File name pattern is required"))
    .min(1)
    .optional()
    .describe(
      "Regex patterns applied to the basename of each direct child file. Any matching pattern archives the file.",
    ),
  fileNameRegex: z
    .string()
    .trim()
    .min(1, "File name regex is required")
    .optional()
    .describe(
      "Deprecated single regex form. Kept for backward compatibility with older workflow configs.",
    ),
  destinationSubfolder: destinationSubfolderSchema
    .prefault("archived")
    .describe("Destination subfolder within each division folder"),
});

export type ArchiveShipAndDebitAzureFilesConfig = z.infer<
  typeof archiveShipAndDebitAzureFilesConfigSchema
>;

export const archiveShipAndDebitAzureFilesStep = defineStepConfig({
  type: "archive_ship_and_debit_azure_files",
  name: "Archive Ship And Debit Azure Files",
  description:
    "Scans configured ship-and-debit Azure folders, matches files by basename regex, and moves them into an archive subfolder.",
  category: "Integration",
  libraryModule: "ShipAndDebit",
  inputSchema: z.object({
    scheduledTime: z
      .string()
      .optional()
      .describe("Scheduled fire time from the schedule trigger"),
  }),

  outputSchema: z.object({
    foldersScanned: z.number().describe("Number of folder prefixes scanned"),
    filesConsidered: z.number().describe("Number of blobs evaluated"),
    filesMatched: z.number().describe("Number of files whose basename matched"),
    filesMoved: z.number().describe("Number of files successfully moved"),
    filesSkipped: z.number().describe("Number of files skipped"),
    fileMoveFailures: z
      .number()
      .describe("Number of files that failed to move"),
  }),

  configSchema: archiveShipAndDebitAzureFilesConfigSchema,

  emits: [] as const,

  execution: {
    startToCloseTimeout: "15 minutes",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "10 seconds",
    },
  },
});
